import React, { useState } from 'react';

const CreateUserForm = ({
  firms = [],
  initialFirmId = '',
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const [form, setForm] = useState({
    name: '',
    username: '',
    password: '',
    firm_id: initialFirmId ? String(initialFirmId) : '',
  });

  const [error, setError] = useState('');

  const activeFirms = firms.filter(
    (firm) => firm.status === 'active'
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.name.trim() || !form.username.trim()) {
      setError('Name and username are required.');
      return;
    }

    if (!form.firm_id) {
      setError('Please select a firm.');
      return;
    }

    if (
      form.password.length < 9 ||
      form.password.length > 128 ||
      !/[A-Z]/.test(form.password) ||
      !/(?:.*[a-z]){3}/.test(form.password) ||
      !/(?:.*[0-9]){4}/.test(form.password) ||
      !/[^A-Za-z0-9]/.test(form.password)
    ) {
      setError(
        'Password must be 9–128 characters and contain an uppercase letter, at least three lowercase letters, four numbers, and a special character.'
      );
      return;
    }

    try {
      await onSubmit({
        name: form.name.trim(),
        username: form.username.trim(),
        password: form.password,
        firm_id: form.firm_id,
      });

      setForm({
        name: '',
        username: '',
        password: '',
        firm_id: initialFirmId ? String(initialFirmId) : '',
      });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Unable to create the user.'
      );
    }
  };

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <div className="admin-form-heading">
        <h2>Create Distributor Account</h2>
        <p>Create login credentials for a user belonging to a firm.</p>
      </div>

      {error && (
        <div className="admin-form-error" role="alert">
          {error}
        </div>
      )}

      <div className="admin-form-grid">
        <div className="admin-form-field">
          <label htmlFor="distributor-name">Full Name *</label>
          <input
            id="distributor-name"
            name="name"
            value={form.name}
            onChange={handleChange}
            maxLength={150}
            required
          />
        </div>

        <div className="admin-form-field">
          <label htmlFor="distributor-username">Username *</label>
          <input
            id="distributor-username"
            name="username"
            value={form.username}
            onChange={handleChange}
            maxLength={100}
            pattern="[a-zA-Z0-9_.-]+"
            title="Use letters, numbers, dots, underscores, and hyphens only."
            autoComplete="off"
            required
          />
        </div>

        <div className="admin-form-field">
          <label htmlFor="distributor-firm">Firm *</label>
          <select
            id="distributor-firm"
            name="firm_id"
            value={form.firm_id}
            onChange={handleChange}
            required
          >
            <option value="">Select a firm</option>

            {activeFirms.map((firm) => (
              <option key={firm.id} value={String(firm.id)}>
                {firm.firm_name} ({firm.firm_code})
              </option>
            ))}
          </select>

          {activeFirms.length === 0 && (
            <small>Create or activate a firm before adding users.</small>
          )}
        </div>

        <div className="admin-form-field">
          <label htmlFor="distributor-password">Initial Password *</label>
          <input
            id="distributor-password"
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            minLength={9}
            maxLength={128}
            autoComplete="new-password"
            required
          />
          <small>
            At least 9 characters, including uppercase, lowercase,
            numbers, and a special character.
          </small>
        </div>
      </div>

      <div className="admin-form-actions">
        {onCancel && (
          <button
            type="button"
            className="admin-button admin-button-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}

        <button
          type="submit"
          className="admin-button admin-button-primary"
          disabled={submitting || activeFirms.length === 0}
        >
          {submitting ? 'Creating...' : 'Create User'}
        </button>
      </div>
    </form>
  );
};

export default CreateUserForm;